import React, { useEffect } from "react";
import { Card, CardContent } from "./ui/card";
import { Phone, Check, Clock } from "lucide-react";
import giftTime from "../assets/images/gift-time.svg";
import analyticsService from "../lib/AnalyticsService";
import { useInteractionTracking } from "../hooks/useAnalytics";

const AADirectCallScreen = ({ buyerData, onComplete }) => {
  const { trackButtonClick } = useInteractionTracking();
  const AADIRECT_PHONE = "4048507169";
  const AADIRECT_PHONE_DISPLAY = "(404) 850-7169";

  useEffect(() => {
    analyticsService.trackPageView("aadirect_call_screen", {
      buyer: buyerData?.name,
    });

    // Automatically attempt to open dialer on mount
    const timer = setTimeout(() => {
      openDialer();
    }, 500); // Small delay to let the screen render first

    return () => clearTimeout(timer);
  }, []);

  const openDialer = () => {
    trackButtonClick("open-dialer", {
      step_name: "aadirect_call_screen",
      phone: AADIRECT_PHONE,
    });

    // Attempt to open the phone dialer
    window.location.href = `tel:${AADIRECT_PHONE}`;
  };

  return (
    <div className="space-y-6">
      {/* Success Header */}
      <div className="text-center space-y-4">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
          <Check className="w-10 h-10 text-green-500" />
        </div>
        <h1 className="text-xl font-bold uppercase">Offer Accepted!</h1>
        <p className="text-gray-600">
          Your reward of{" "}
          <span className="font-bold text-[#7B61FF]">
            ${buyerData?.offer?.amount?.toFixed(2) || "0.00"}
          </span>{" "}
          is now pending
        </p>
      </div>

      {/* Buyer Logo */}
      {buyerData?.logo && (
        <div className="flex justify-center">
          <div className="bg-gray-50 p-4 rounded-lg">
            <img
              src={buyerData.logo}
              alt={buyerData.name}
              className="h-16 w-auto object-contain"
            />
          </div>
        </div>
      )}

      {/* Pending Reward Notice */}
      <Card className="shadow-none bg-[#CEC5FF66] border-none rounded-[20px]">
        <CardContent className="p-5">
          <div className="flex items-center gap-3">
            <img src={giftTime} alt="pending reward" />
            <p className="text-base text-darkPurple">
              Your reward is pending until your profile is approved
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Next Step Instructions */}
      <Card className="border-2 border-[#7B61FF] bg-white rounded-[20px]">
        <CardContent className="p-6">
          <div className="text-center space-y-4">
            <Phone className="w-12 h-12 text-[#7B61FF] mx-auto" />
            <div>
              <h2 className="text-lg font-bold text-gray-900 mb-2">
                Next Step: Call {buyerData?.name || "AADirect"}
              </h2>
              <p className="text-gray-600 text-sm">
                An expert is ready to help you get your personalized insurance
                quote
              </p>
            </div>

            {/* Phone Number Display */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Call this number:</p>
              <p className="text-2xl font-bold text-gray-900">
                {AADIRECT_PHONE_DISPLAY}
              </p>
            </div>

            {/* Call Now Button */}
            <button
              onClick={openDialer}
              className="w-full flex items-center justify-center gap-2 uppercase font-bold bg-purpleGradient text-white px-4 py-3 rounded-[10px] hover:bg-purpleGradientDark transition-colors"
            >
              <Phone className="w-5 h-5" />
              <span>Call Now</span>
            </button>

            <p className="text-xs text-gray-500">
              Tap the button above to dial automatically
            </p>
          </div>
        </CardContent>
      </Card>

      {/* What to Expect */}
      <Card className="border-gray-200 rounded-[20px]">
        <CardContent className="p-5">
          <h3 className="font-bold text-gray-900 mb-3">What to Expect:</h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
              <span>
                An {buyerData?.name || "AADirect"} expert will review your
                Ownli Insurance Profile
              </span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
              <span>They'll provide personalized quote options</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
              <span>Your reward will be approved after profile validation</span>
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Skip Option */}
      {onComplete && (
        <button
          onClick={onComplete}
          className="w-full text-center text-gray-600 hover:text-gray-900 text-sm"
        >
          I'll call later
        </button>
      )}
    </div>
  );
};

export default AADirectCallScreen;